 import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import html2canvas from "html2canvas";
import { Camera, MapPin, Clock, Thermometer, Download, Loader2 } from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

// Your actual Google Maps API Key
const GOOGLE_MAPS_API_KEY = "AIzaSyDVUbdHE7cVJYbA9SXThZuSwYo2mxZyHEk";

const GpsCamera = () => {
  const webcamRef = useRef(null);
  const captureRef = useRef(null);

  const [data, setData] = useState({
    lat: null,
    lon: null,
    address: "",
    temperature: "",
  });

  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [captured, setCaptured] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const fetchLocationData = async () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        alert("Geolocation not supported");
        reject("No geolocation");
        return;
      }

      navigator.geolocation.getCurrentPosition(async (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lon = position.coords.longitude.toFixed(6);

        let address = "Address not found";
        let temperature = "N/A";

        try {
          const geoRes = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const geoData = await geoRes.json();
          if (geoData.status === "OK" && geoData.results.length > 0) {
            address = geoData.results[0].formatted_address;
          }
        } catch (err) {
          console.error("Google Geocoding error:", err);
        }

        try {
          const weatherResponse = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&timezone=auto`
          );
          const weatherData = await weatherResponse.json();
          temperature = `${weatherData.current_weather.temperature} °C`;
        } catch (err) {
          console.error("Weather fetch error:", err);
        }

        const result = { lat, lon, address, temperature };
        setData(result);
        resolve(result);
      }, reject);
    });
  };

  const capture = async () => {
    setLoading(true);
    setCaptured(false);

    const screenshot = webcamRef.current.getScreenshot();
    setImageSrc(screenshot);

    await fetchLocationData();

    setTimeout(async () => {
      if (captureRef.current) {
        const canvas = await html2canvas(captureRef.current, {
          useCORS: true,
          allowTaint: true,
          logging: true,
          scale: 1,
        });
        const finalImg = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = finalImg;
        link.download = `photo_${new Date().toISOString()}.png`;
        link.click();
        setCaptured(true);
        setLoading(false);
      }
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-3 border border-white/20">
          <Camera className="w-8 h-8 text-cyan-400" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            ACSPL GPS Camera
          </h1>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-8 max-w-7xl mx-auto">
        {/* Webcam Section */}
        <div className="flex-1">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-6">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/png"
                className="w-full h-auto"
                videoConstraints={{
                  facingMode: "environment",
                }}
              />
              <div className="absolute inset-0 border-4 border-cyan-400/30 rounded-2xl pointer-events-none"></div>
            </div>

            <button
              onClick={capture}
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 
                         disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed
                         text-white font-semibold py-4 px-6 rounded-2xl transition-all duration-300 
                         transform hover:scale-105 active:scale-95 shadow-lg
                         flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Capturing GPS Data...</span>
                </>
              ) : (
                <>
                  <Camera className="w-6 h-6" />
                  <span>Capture With ACSPL Camera</span>
                </>
              )}
            </button>

            {captured && (
              <div className="mt-4 bg-green-500/20 border border-green-400/30 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-center gap-3 text-green-300">
                  <Download className="w-5 h-5" />
                  <span className="font-medium">Photo saved to your device!</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview Section */}
        <div className="flex-1">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-400" />
              Preview
            </h2>

            <div
              ref={captureRef}
              className="relative rounded-2xl overflow-hidden shadow-2xl bg-black"
            >
              {imageSrc ? (
                <>
                  <img
                    src={imageSrc}
                    alt="Captured"
                    className="w-full h-auto object-contain"
                  />

                  {/* Info Overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/90 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                      <div className="flex items-start gap-4">
                        {/* Google Map */}
                        {isLoaded && data.lat && (
                          <div className="w-20 h-16 border-2 border-cyan-400/50 rounded-xl overflow-hidden flex-shrink-0">
                            <GoogleMap
                              center={{ lat: parseFloat(data.lat), lng: parseFloat(data.lon) }}
                              zoom={13}
                              mapContainerStyle={{ width: "100%", height: "100%" }}
                              options={{
                                disableDefaultUI: true,
                                gestureHandling: "none",
                              }}
                            >
                              <Marker
                                position={{
                                  lat: parseFloat(data.lat),
                                  lng: parseFloat(data.lon),
                                }}
                              />
                            </GoogleMap>
                          </div>
                        )}

                        {/* Text Info */}
                        <div className="text-white text-sm space-y-2 flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                            <span className="break-words">
                              {data.address || "Location not available"}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-cyan-400">🗺</span>
                            <span className="font-mono text-xs">
                              {data.lat || "--"}, {data.lon || "--"}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs">{new Date().toLocaleString()}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Thermometer className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs">{data.temperature || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Watermark */}
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-cyan-500/80 to-purple-600/80 
                                  backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full border border-white/20">
                    Powered By ACSPL
                  </div>
                </>
              ) : (
                <div className="aspect-video flex items-center justify-center text-gray-400 bg-gray-800/50 rounded-2xl">
                  <div className="text-center">
                    <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Captured photo will appear here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GpsCamera;
